import React, {useEffect, useMemo, useRef, useState} from 'react';
import s from './GeoStatistics.module.scss'
import SvgSelector from "../../../../../components/SvgSelector/SvgSelector";
import EmptyList from "../../../../../components/EmptyList/EmptyList";
import CustomSkeleton from "../../../../../components/UiKit/CustomSkeleton/CustomSkeleton";
import {stringToDayJs} from "../../../../../utils/helpers/formatTime";
import {useSearchParams} from "react-router-dom";
import Pagination from "../../../../../components/Pagination/Pagination";
import {
    geoStatsOrderingList,
    params,
    sortDateOptions,
} from "../../../../../utils/vars/params";
import {useDispatch, useSelector} from "react-redux";
import {globalDataSelector} from "../../../../../redux/slices/globalDataSlice";
import {
    downloadGeoStats,
    downloadUsersCommonStats,
    fetchGeoStats,
    resetTotalCount,
    statisticsSelector
} from "../../../../../redux/slices/statisticsSlice";
import {useTranslation} from "react-i18next";
import useGetSearchParams from "../../../../../hooks/useGetSearchParams";
import useSetSearchParams from "../../../../../hooks/useSetSearchParams";
import {
    created_at_after, created_at_before,
    geo,
    page,
    sort
} from "../../../../Authorized/Statistics/IOS/IOSClicksStats/IOSClicksStats";
import {fetchGeoAppStatsFilters} from "../../../../../api/helpers/fetchFilters";
import {getDefaultParams} from "../../../../../api/helpers/scripts";
import {
    downloadFileTypesList,
    INVALID_PAGE_ERROR, snackbar_error,
    snackbar_info, snackbar_success,
    snackbar_warning
} from "../../../../../utils/vars/staticVariables";
import {onSetPageSize} from "../../../../../utils/helpers/statisticsHelper";
import {cleanSearchParams, hasNonEmptyParams} from "../../../../../utils/helpers/cleanFilters";
import {MuiDatePicker} from "../../../../../components/DatePickers/MuiDatePicker";
import GeoSelect from "../../../../../components/Selects/GeoSelect";
import ParamsSort from "../../../../../components/ParamsSort/ParamsSort";
import {PageSizeDropdown} from "../../../../../components/UiKit/PageSizeDropdown/PageSizeDropdown";
import {capitalizeWord} from "../../../../../utils/helpers/capitalizeWord";
import {downloadTableFromBinaryData} from "../../../../../utils/helpers/downloadTableFromBinaryData";
import {useOutsideAndEscClose} from "../../../../../hooks/useOutsideAndEscClose";
import useCustomSnackbar from "../../../../../hooks/useCustomSnackbar";
import {useSnackbar} from "notistack";


const GeoStatistics = () => {
    const fileFormatDropdownRef = useRef(null);

    const [sortDate, setSortDate] = useState(sortDateOptions[0]);
    const [selectedGeos, setSelectedGeos] = useState([]);
    const [isDownloadFormatPopupActive, setIsDownloadFormatPopupActive] = useState(false);
    const [downloadFormat, setDownloadFormat] = useState(downloadFileTypesList[0]);

    const dispatch = useDispatch();

    const {geoList, isCollapsedSidebar, pageSizeValue} =
        useSelector(globalDataSelector);

    const {
        statisticsList,
        loading,
        totalCount,
        downloadGeoStatisticsPending
    } = useSelector(statisticsSelector);

    const {t} = useTranslation();
    const showSnackbar = useCustomSnackbar();
    const {closeSnackbar} = useSnackbar();

    const [searchParams, setSearchParams] = useSearchParams();

    const {
        sort: sortValue,
        page: pageValue,
        created_at_after: createdAtAfterParam,
        created_at_before: createdAtBeforeParam,
    } = useGetSearchParams();

    const updateSearchParams = useSetSearchParams(setSearchParams);

    useOutsideAndEscClose(fileFormatDropdownRef, setIsDownloadFormatPopupActive);

    const geoListOptions = useMemo(() => {
        return geoList.map((item) => ({
            label: item.name,
            value: item.code,
            icon: item.flag,
        }));
    }, [geoList]);

    useEffect(() => {
        fetchGeoAppStatsFilters(dispatch);
    }, [dispatch]);


    useEffect(() => {
        if (
            createdAtAfterParam ||
            createdAtBeforeParam ||
            sortDate.value === "custom_date"
        ) {
            setSortDate(sortDateOptions.find((item) => item.value === "custom_date"));
        } else {
            if (!sortValue) {
                setSortDate(sortDateOptions[0]);
            } else {
                setSortDate(sortDateOptions.find(({value}) => value === sortValue));
            }
        }
    }, [searchParams]);

    useEffect(() => {
        const newGeoListOptions = searchParams
            .get(geo)
            ?.split(",")
            .filter((value) => value !== "")
            .map((value) => {
                return geoListOptions.find((item) => item.value === value);
            });

        if (!newGeoListOptions || newGeoListOptions.length === 0) {
            setSelectedGeos([]);
        } else {
            setSelectedGeos(newGeoListOptions);
        }
    }, [searchParams, geoListOptions]);

    useEffect(() => {
        const promise = dispatch(
            fetchGeoStats(
                getDefaultParams({
                    searchParams,
                    sort,
                    defaultPageSize: pageSizeValue,
                }),
            ),
        );

        promise.unwrap().catch((err) => {
            if (err?.message === INVALID_PAGE_ERROR) {
                updateSearchParams(page, 1);
            }
        });

        return () => {
            promise.abort();
        };
    }, [dispatch, searchParams, pageSizeValue]);

    useEffect(() => {
        return () => dispatch(resetTotalCount());
    }, [dispatch]);

    const handleSetPageSize = (size) => {
        onSetPageSize(dispatch, size);
    };

    const cleanFilters = () => {
        cleanSearchParams(searchParams, setSearchParams)
        setSelectedGeos([]);
    };

    const onDownloadStatistics = async () => {
        const downloadSnackbar = showSnackbar(
            t("stats_downloading"),
            snackbar_info,
            true,
        );

        const downloadStatisticsResult = await dispatch(downloadGeoStats({params: searchParams, file_type: downloadFormat?.value}));

        if (downloadStatisticsResult?.payload?.status === 200) {
            let statsDateRange

            statsDateRange = `${createdAtAfterParam}-${createdAtBeforeParam}`

            downloadTableFromBinaryData(downloadStatisticsResult?.payload?.data, "Geo_Common_Statistics", downloadFormat?.value, statsDateRange);
            closeSnackbar(downloadSnackbar);
            showSnackbar(t("stats_downloaded"), snackbar_success);
        } else {
            closeSnackbar(downloadSnackbar);
            showSnackbar(t("failed_to_download_stats"), snackbar_error);
        }
    };

    const theadBlock = <thead>
    <tr>
        {geoStatsOrderingList.map(({ordering, name}) => (
            <th key={name}>
                <div className={s.headerContent}>
                    {t(name)}

                    {
                        ordering && <ParamsSort
                            sortBy={ordering}
                            searchParams={searchParams}
                            setSearchParams={setSearchParams}
                        />
                    }
                </div>
            </th>
        ))}
    </tr>
    </thead>

    return (
        <div className={s.tabContentWrapper}>
            <div className={s.filterGradientBgWrapper}>
                <div className={s.filtersWrapper}>
                    <div className={s.filters}>
                        <div className={s.datePickersWrapper}>
                            <div className={s.datePicker}>
                                <MuiDatePicker
                                    label={t("date_from")}
                                    searchParamValue={created_at_after}
                                    updateSearchParams={updateSearchParams}
                                />
                            </div>
                            <div className={s.datePicker}>
                                <MuiDatePicker
                                    label={t("date_to")}
                                    searchParamValue={created_at_before}
                                    updateSearchParams={updateSearchParams}
                                />
                            </div>
                        </div>

                        <div className={s.selectWrapper}>
                            <div className={s.selectIconWrapper}>
                                <SvgSelector id="geo-select-icon"/>
                            </div>
                            <GeoSelect
                                isClearable={true}
                                value={selectedGeos}
                                options={geoListOptions}
                                onChange={(values) =>
                                    updateSearchParams(
                                        geo,
                                        values.map((el) => el.value).join(","),
                                    )
                                }
                                placeholder={t("geo")}
                                isMulti={true}
                            />
                        </div>

                        <div className={s.downloadBtnWrapper}>

                            <button
                                className={s.primaryBtn}
                                onClick={() => {
                                    if (statisticsList.length > 0) {
                                        onDownloadStatistics()
                                    } else {
                                        showSnackbar(t('no_records_available'), snackbar_warning)
                                    }
                                }}
                                disabled={downloadGeoStatisticsPending}
                            >
                                <SvgSelector id="download-btn-icon"/>
                                {t("download")}
                            </button>
                            <button className={s.arrowBtn} onClick={(e) => {
                                e.stopPropagation();
                                setIsDownloadFormatPopupActive((prev) => !prev);
                            }}>
                                <SvgSelector id='language-dropdown-arrow'/>
                            </button>

                            {isDownloadFormatPopupActive && (
                                <ul className={s.langList} ref={fileFormatDropdownRef} tabIndex={0}>
                                    {downloadFileTypesList.map((type, index) => (
                                        <li
                                            onClick={() => {
                                                setDownloadFormat(type)
                                                setIsDownloadFormatPopupActive(false)
                                            }}
                                            key={index}
                                        >
                                            <button className={s.langOptionBtn}>
                                                {type.label}
                                                {type.value === downloadFormat?.value && (
                                                    <SvgSelector id="language-check-icon"/>
                                                )}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {hasNonEmptyParams(searchParams) && (
                            <div className={s.cleanBtnContainer}>
                                <button className={s.blueBorderBtn} onClick={cleanFilters}>
                                    {t("clear_button")}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className={s.tableContainer}>
                <div className={s.pageInnerContent}>
                    <div className={s.innerContentContainer}>
                        {totalCount === 0 && !loading ? (
                            <EmptyList/>
                        ) : loading ? (
                            <section className={s.body}>
                                <table>
                                    {theadBlock}
                                </table>
                                <div className={s.skeletonWrapper}>
                                    <CustomSkeleton styles={s.skeletonTable}/>
                                </div>
                            </section>
                        ) : (
                            <section className={s.body}>
                                <table>
                                    {theadBlock}
                                    <tbody>
                                    {statisticsList?.map(
                                        (
                                            {
                                                android_installs,
                                                geo,
                                                ios_installs,
                                                pwa_installs,
                                                total_installs
                                            },
                                            index,
                                        ) => {
                                            const geoFlag = geoList.find(
                                                (geoItem) => geoItem.code === geo,
                                            )?.flag;

                                            return (
                                                <tr key={index} className={s.scrollTable}>
                                                    <td className={s.geoTd}>
                                                    <span className={s.value}>
                                                      <span
                                                          className={s.flagWrapper}
                                                          style={
                                                              geoFlag
                                                                  ? {
                                                                      backgroundImage: `url(${geoFlag})`,
                                                                  }
                                                                  : {
                                                                      background: "grey",
                                                                  }
                                                          }
                                                      ></span>
                                                      <span>{geo}</span>
                                                    </span>
                                                    </td>
                                                    <td>{ios_installs}</td>
                                                    <td>{android_installs}</td>
                                                    <td>{pwa_installs}</td>
                                                    <td>{total_installs}</td>
                                                </tr>
                                            );
                                        },
                                    )}
                                    </tbody>
                                </table>
                            </section>
                        )}
                    </div>
                </div>

                <div
                    className={`${s.paginationContainerStatistics} ${isCollapsedSidebar === "true" ? s.collapsed : ""}`}
                >
                    <div className={s.countPageSizeContainer}>
                        <div className={s.newRecordsCount}>
                            {t("total")}: {!loading && <span>{totalCount}</span>}
                        </div>
                        <div className={s.pageSizeDropdownContainer}>
                            <PageSizeDropdown
                                setSize={handleSetPageSize}
                                size={pageSizeValue}
                            />
                        </div>
                    </div>
                    {totalCount > pageSizeValue && (
                        <Pagination
                            currentPage={parseInt(pageValue) || 1}
                            setCurrentPage={(page) => {
                                updateSearchParams(params.page, page);
                            }}
                            total={totalCount}
                            limit={pageSizeValue}
                        />
                    )}
                </div>
            </div>

        </div>
    )
}

export default GeoStatistics;